import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity,
  Calculator,
  CircleDollarSign,
  Plus,
  RefreshCw,
  Save,
  Target,
  Trash2,
  TrendingUp,
  WalletCards,
} from "lucide-react";

const emptyCost = () => ({
  id: `cost-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  category: "Operasional",
  description: "",
  amount: 0,
});

const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const pct = (value) =>
  `${Number(value || 0).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%`;

export default function PlatformBepCalculator() {
  const [data, setData] = useState(null);
  const [fixedCosts, setFixedCosts] = useState([]);
  const [variableCostPerTenant, setVariableCostPerTenant] = useState(0);
  const [initialInvestment, setInitialInvestment] = useState(0);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/bep");
      const payload = response.data || {};
      setData(payload);
      setFixedCosts((payload.settings?.fixed_costs || []).map((row) => ({ ...row })));
      setVariableCostPerTenant(asNumber(payload.settings?.variable_cost_per_tenant));
      setInitialInvestment(asNumber(payload.settings?.initial_investment));
      setNote(payload.settings?.note || "");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Data BEP gagal dimuat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const localFixedTotal = useMemo(
    () => fixedCosts.reduce((sum, row) => sum + Math.max(0, asNumber(row.amount)), 0),
    [fixedCosts],
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        fixed_costs: fixedCosts.map((row) => ({
          id: row.id,
          category: String(row.category || "").trim(),
          description: String(row.description || "").trim(),
          amount: Math.max(0, asNumber(row.amount)),
        })),
        variable_cost_per_tenant: Math.max(0, asNumber(variableCostPerTenant)),
        initial_investment: Math.max(0, asNumber(initialInvestment)),
        note: String(note || "").trim(),
      };
      const response = await api.put("/admin/bep", payload);
      setData(response.data || null);
      setFixedCosts((response.data?.settings?.fixed_costs || []).map((row) => ({ ...row })));
      toast.success("Konfigurasi BEP berhasil disimpan");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Konfigurasi BEP gagal disimpan");
    } finally {
      setSaving(false);
    }
  };

  const updateCost = (index, field, value) => {
    setFixedCosts((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const metrics = data?.metrics || {};
  const bepReached = Boolean(metrics.bep_reached);
  const canCalculateBep = metrics.bep_tenants !== null && metrics.bep_tenants !== undefined;
  const progress = Math.max(0, Math.min(100, asNumber(metrics.bep_progress_percent)));

  return (
    <section
      className="mt-10 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
      data-testid="platform-bep-section"
    >
      <div className="border-b border-slate-200 bg-slate-950 p-6 text-white sm:p-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">
              <Calculator className="h-4 w-4" /> Financial Planning
            </div>
            <h2 className="mt-3 font-display text-3xl font-black tracking-[-0.04em] sm:text-4xl">
              Break Even Point KRAYA
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
              Revenue dihitung otomatis dari subscription aktif berbayar. Trial tidak dihitung.
              Paket tahunan dinormalisasi menjadi Monthly Recurring Revenue.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-white/10 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="p-8 text-sm text-slate-500">Memuat perhitungan BEP…</div>
      ) : (
        <>
          <div className="grid gap-px bg-slate-200 sm:grid-cols-2 xl:grid-cols-4">
            {[
              {
                label: "MRR Aktual",
                value: formatIDR(asNumber(metrics.mrr)),
                sub: `${asNumber(metrics.paid_tenants)} mitra berbayar`,
                icon: CircleDollarSign,
              },
              {
                label: "ARPU / Mitra",
                value: formatIDR(asNumber(metrics.arpu)),
                sub: "Pendapatan rata-rata per mitra",
                icon: TrendingUp,
              },
              {
                label: "BEP Mitra",
                value: canCalculateBep ? `${asNumber(metrics.bep_tenants)} Mitra` : "Belum dapat dihitung",
                sub: canCalculateBep
                  ? `Gap ${asNumber(metrics.gap_tenants)} mitra`
                  : "Contribution margin harus > 0",
                icon: Target,
              },
              {
                label: "Laba/Rugi Operasional",
                value: formatIDR(asNumber(metrics.operating_profit)),
                sub: bepReached ? "Sudah melewati BEP" : "Belum mencapai BEP",
                icon: Activity,
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="bg-white p-5 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                      {card.label}
                    </div>
                    <Icon className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="mt-3 font-display text-2xl font-black tracking-tight text-slate-950">
                    {card.value}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{card.sub}</div>
                </div>
              );
            })}
          </div>

          <div className="grid gap-6 p-5 sm:p-7 xl:grid-cols-[1.25fr_0.75fr]">
            <div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">
                    Cost Structure
                  </div>
                  <h3 className="mt-1 text-xl font-black text-slate-950">Biaya Tetap Bulanan</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setFixedCosts((rows) => [...rows, emptyCost()])}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-bold text-slate-700 transition hover:border-blue-300 hover:bg-blue-50"
                >
                  <Plus className="h-4 w-4" /> Tambah Biaya
                </button>
              </div>

              <div className="mt-5 space-y-3">
                {fixedCosts.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500">
                    Belum ada biaya tetap. Tambahkan biaya seperti server, domain, security, developer, support, atau operasional lainnya.
                  </div>
                )}

                {fixedCosts.map((row, index) => (
                  <div
                    key={row.id || index}
                    className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[0.8fr_1.35fr_0.8fr_auto]"
                  >
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Kategori</label>
                      <input
                        value={row.category || ""}
                        onChange={(event) => updateCost(index, "category", event.target.value)}
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-400"
                        placeholder="Infrastruktur"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Uraian</label>
                      <input
                        value={row.description || ""}
                        onChange={(event) => updateCost(index, "description", event.target.value)}
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-400"
                        placeholder="VPS Production"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Biaya / Bulan</label>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={row.amount ?? 0}
                        onChange={(event) => updateCost(index, "amount", event.target.value)}
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-400"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => setFixedCosts((rows) => rows.filter((_, i) => i !== index))}
                        className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50"
                        aria-label="Hapus biaya"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between rounded-2xl bg-slate-950 px-5 py-4 text-white">
                <span className="text-sm font-semibold">Total Fixed Cost / Bulan</span>
                <strong className="font-display text-xl">{formatIDR(localFixedTotal)}</strong>
              </div>
            </div>

            <div className="space-y-5">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
                <div className="flex items-center gap-2">
                  <WalletCards className="h-5 w-5 text-blue-600" />
                  <h3 className="font-black text-slate-950">Parameter BEP</h3>
                </div>

                <label className="mt-5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                  Biaya Variabel / Mitra / Bulan
                </label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={variableCostPerTenant}
                  onChange={(event) => setVariableCostPerTenant(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400"
                />

                <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-slate-500">
                  Investasi Awal
                </label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={initialInvestment}
                  onChange={(event) => setInitialInvestment(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400"
                />

                <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-slate-500">Catatan</label>
                <textarea
                  rows={3}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  className="mt-2 w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400"
                  placeholder="Catatan asumsi biaya BEP..."
                />

                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Menyimpan…" : "Simpan & Hitung Ulang"}
                </button>
              </div>

              <div
                className={`rounded-3xl border p-5 sm:p-6 ${
                  bepReached ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className={`text-xs font-bold uppercase tracking-[0.18em] ${bepReached ? "text-emerald-700" : "text-amber-700"}`}>
                      Status BEP
                    </div>
                    <div className={`mt-1 text-2xl font-black ${bepReached ? "text-emerald-950" : "text-amber-950"}`}>
                      {bepReached ? "SUDAH BEP" : "BELUM BEP"}
                    </div>
                  </div>
                  <Target className={`h-8 w-8 ${bepReached ? "text-emerald-600" : "text-amber-600"}`} />
                </div>

                <div className="mt-5 h-3 overflow-hidden rounded-full bg-white/80">
                  <div
                    className={`h-full rounded-full transition-all ${bepReached ? "bg-emerald-500" : "bg-amber-500"}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="mt-2 flex justify-between text-xs font-semibold text-slate-600">
                  <span>{pct(metrics.bep_progress_percent)}</span>
                  <span>
                    {asNumber(metrics.paid_tenants)}
                    {canCalculateBep ? ` / ${asNumber(metrics.bep_tenants)}` : ""} mitra
                  </span>
                </div>

                <div className="mt-5 space-y-3 border-t border-black/10 pt-5 text-sm">
                  <div className="flex justify-between gap-4"><span className="text-slate-600">Fixed Cost</span><strong>{formatIDR(asNumber(metrics.fixed_cost_total))}</strong></div>
                  <div className="flex justify-between gap-4"><span className="text-slate-600">Variable Cost Total</span><strong>{formatIDR(asNumber(metrics.variable_cost_total))}</strong></div>
                  <div className="flex justify-between gap-4"><span className="text-slate-600">Contribution / Mitra</span><strong>{formatIDR(asNumber(metrics.contribution_margin_per_tenant))}</strong></div>
                  <div className="flex justify-between gap-4"><span className="text-slate-600">BEP Revenue</span><strong>{canCalculateBep ? formatIDR(asNumber(metrics.bep_revenue)) : "—"}</strong></div>
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-600">Payback Investasi</span>
                    <strong>
                      {metrics.payback_months
                        ? `${Number(metrics.payback_months).toLocaleString("id-ID", { maximumFractionDigits: 1 })} bulan`
                        : "—"}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
